import { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useLocation } from "react-router-dom";
import { askIdwalAIService, ChatMessage, ChatSession } from "../../services/AskIdwalAIService";
import { SearchIcon } from "./SearchIcon";
import { useAuthenticator } from "@aws-amplify/ui-react";
import ReactMarkdown from 'react-markdown';

interface AskIdwalAIProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AskIdwalAI = ({ isOpen, onClose }: AskIdwalAIProps) => {
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [session, setSession] = useState<ChatSession | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isUnavailable, setIsUnavailable] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const location = useLocation();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Initialize session when panel opens
    useEffect(() => {
        if (isOpen) {
            initializeSession();
        }
    }, [isOpen, user]);

    const initializeSession = async () => {
        try {
            const inspectionId = getInspectionId();
            const newSession = await askIdwalAIService.createSession(user, inspectionId);
            setSession(newSession);
            setIsConnected(true);
            setIsUnavailable(false);
        } catch (err: any) {
            console.error('Failed to initialize session:', err);
            if (err.message === 'SERVICE_UNAVAILABLE') {
                setIsUnavailable(true);
                setIsConnected(false);
                setError('System unavailable');
            } else {
                setError('Failed to connect to chat service');
            }
        }
    };

    const getInspectionId = () => {
        const pathParts = location.pathname.split('/');
        if (pathParts[1] === 'inspection' && pathParts[2]) {
            return pathParts[2];
        }
        return undefined;
    };

    const sendMessage = async (text?: string) => {
        const messageText = text || message;
        if (!messageText.trim() || isLoading || !session || isUnavailable) return;

        // Immediately show user message
        const userMessage: ChatMessage = {
            id: `temp_${Date.now()}`,
            text: messageText,
            isUser: true,
            timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, userMessage]);
        setMessage('');
        setIsLoading(true);
        setError(null);

        try {
            const inspectionId = getInspectionId();
            
            // Send chat message with inspectionId
            const response = await askIdwalAIService.sendChat(session.sessionId, messageText, inspectionId);
            
            // Update with actual messages from response
            setMessages(prev => {
                // Remove temp user message and add real messages
                const withoutTemp = prev.filter(m => m.id !== userMessage.id);
                return [...withoutTemp, response.message, response.aiResponse];
            });
        } catch (err: any) {
            console.error('Error sending message:', err);
            
            if (err.message === 'SERVICE_UNAVAILABLE') {
                setIsUnavailable(true);
                setIsConnected(false);
                setError('System unavailable');
            } else {
                setError(err.message || 'Failed to send message');
            }
            
            // Remove the user message on error
            setMessages(prev => prev.filter(m => m.id !== userMessage.id));
            
            // Clear error after 5 seconds
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const resetChat = () => {
        setMessages([]);
        setMessage('');
        setSession(null);
    };

    const handleFeedback = async (messageId: string, messageTimestamp: string | undefined, rating: 'positive' | 'negative') => {
        if (!session || !messageTimestamp) return;

        try {
            await askIdwalAIService.submitFeedback(session.sessionId, messageTimestamp, rating);

            // Update the message's feedback state
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, feedback: rating } : msg
            ));
        } catch (err) {
            console.error('Failed to submit feedback:', err);
        }
    };

    const predefinedQuestions = [
        "What are the key findings in my latest report?",
        "Show me defects by severity level"
    ];

    if (!isOpen) return null;

    const showInitialView = messages.length === 0;
    const showBackButton = !showInitialView || message.trim().length > 0;

    return (
        <>
            <style>
                {`
                    @keyframes typing {
                        0%, 60%, 100% {
                            transform: translateY(0);
                            opacity: 0.4;
                        }
                        30% {
                            transform: translateY(-10px);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000
                }}
                onClick={onClose}
            />
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '400px',
                    height: '100%',
                    backgroundColor: 'white',
                    boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
                    zIndex: 1001,
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div style={{ 
                    padding: '20px', 
                    borderBottom: '1px solid #ddd',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {showBackButton && (
                            <Button 
                                icon="pi pi-arrow-left" 
                                className="p-button-text" 
                                onClick={resetChat}
                            />
                        )}
                        <h3 style={{ margin: 0 }}>Ask Idwal AI</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
                            <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: isUnavailable ? '#f44336' : (isConnected ? '#4caf50' : '#f44336'),
                                display: 'inline-block'
                            }}></span>
                            <span style={{ color: '#666' }}>
                                {isUnavailable ? 'System unavailable' : (isConnected ? 'Connected' : 'Connecting...')}
                            </span>
                        </div>
                    </div>
                    <Button 
                        icon="pi pi-times" 
                        className="p-button-text" 
                        onClick={onClose}
                    />
                </div>

                {/* Error Banner */}
                {error && (
                    <div style={{
                        padding: '10px 20px',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        fontSize: '12px',
                        borderBottom: '1px solid #ef9a9a'
                    }}>
                        {error}
                    </div>
                )}

                {/* Middle */}
                <div style={{ 
                    flex: 1, 
                    padding: '20px', 
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {showInitialView ? (
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            textAlign: 'center'
                        }}>
                            <div style={{ marginBottom: '20px' }}>
                                <SearchIcon />
                            </div>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>How can I help?</h4>
                            <p style={{ color: '#666', marginBottom: '30px', fontSize: '14px' }}>
                                Ask me anything to explore report insights.
                            </p>
                            {predefinedQuestions.map((question, index) => (
                                <Button
                                    key={index}
                                    label={question}
                                    icon="pi pi-chevron-right"
                                    iconPos="right"
                                    className="p-button-outlined"
                                    style={{ 
                                        width: '100%', 
                                        marginBottom: '10px',
                                        justifyContent: 'space-between'
                                    }}
                                    onClick={() => sendMessage(question)}
                                    disabled={isUnavailable || !isConnected}
                                />
                            ))}
                        </div>
                    ) : (
                        <div id="messages" style={{ 
                            flex: 1, 
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '10px', 
                            border: '1px solid #ddd', 
                            borderRadius: '8px',
                            backgroundColor: '#f9f9f9',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {messages.map((msg, index) => (
                                <div key={index}>
                                    <div style={{
                                        marginBottom: '10px',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        backgroundColor: msg.isUser ? '#009EB2' : '#e9ecef',
                                        color: msg.isUser ? 'white' : 'black',
                                        alignSelf: msg.isUser ? 'flex-end' : 'flex-start',
                                        maxWidth: '80%',
                                        marginLeft: msg.isUser ? 'auto' : '0',
                                        marginRight: msg.isUser ? '0' : 'auto',
                                        fontSize: '12px',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}>
                                        {msg.isUser ? msg.text : <ReactMarkdown>{msg.text}</ReactMarkdown>}
                                    </div>
                                    {!msg.isUser && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '10px' }}>
                                            <Button
                                                icon="pi pi-copy"
                                                label="Copy"
                                                text
                                                size="small"
                                                onClick={() => navigator.clipboard.writeText(msg.text)}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px'
                                                }}
                                            />
                                            <Button
                                                icon="pi pi-thumbs-up"
                                                text
                                                size="small"
                                                onClick={() => handleFeedback(msg.id, msg.messageTimestamp, 'positive')}
                                                disabled={!msg.messageTimestamp}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    color: msg.feedback === 'positive' ? '#4caf50' : undefined,
                                                    backgroundColor: msg.feedback === 'positive' ? '#e8f5e9' : undefined,
                                                    borderRadius: '4px'
                                                }}
                                            />
                                            <Button
                                                icon="pi pi-thumbs-down"
                                                text
                                                size="small"
                                                onClick={() => handleFeedback(msg.id, msg.messageTimestamp, 'negative')}
                                                disabled={!msg.messageTimestamp}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '4px 8px',
                                                    color: msg.feedback === 'negative' ? '#f44336' : undefined,
                                                    backgroundColor: msg.feedback === 'negative' ? '#ffebee' : undefined,
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                            {isLoading && (
                                <div style={{
                                    marginBottom: '10px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    backgroundColor: '#e9ecef',
                                    color: 'black',
                                    maxWidth: '80%',
                                    marginLeft: '0',
                                    marginRight: 'auto'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        gap: '4px',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: '#666',
                                            animation: 'typing 1.4s infinite ease-in-out',
                                            animationDelay: '0s'
                                        }}></div>
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: '#666',
                                            animation: 'typing 1.4s infinite ease-in-out',
                                            animationDelay: '0.2s'
                                        }}></div>
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: '#666',
                                            animation: 'typing 1.4s infinite ease-in-out',
                                            animationDelay: '0.4s'
                                        }}></div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                {/* Bottom */}
                <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', height: '47px' }}>
                        <InputTextarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={isUnavailable ? "System unavailable" : (isConnected ? "Type your message..." : "Connecting...")}
                            rows={2}
                            style={{ flex: 1 }}
                            disabled={!isConnected || isLoading || isUnavailable}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                        />
                        <Button 
                            icon="pi pi-send" 
                            onClick={() => sendMessage()}
                            disabled={!isConnected || !message.trim() || isLoading || isUnavailable}
                            loading={isLoading}
                            style={{
                                borderRadius: '8px',
                                opacity: 0.4,
                                background: '#009EB2',
                                height: '47px'
                            }}
                        />
                    </div>
                    <p style={{ 
                        fontSize: '11px', 
                        color: '#666', 
                        margin: 0,
                        textAlign: 'center'
                    }}>
                        AI can be inaccurate—review carefully. <a 
                            href="#" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#009EB2' }}
                        >
                            Using Idwal AI
                        </a>
                    </p>
                </div>
            </div>
        </>
    );
};
